import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { LocalTripFormData, FormUpdateParams } from '@/types/forms'
import DateTimePicker from '@/components/common/DateTimePicker'
import type { OccupiedPeriod } from '@/types/travel'

interface LocalTripFormProps {
  formData: LocalTripFormData
  onChange: (updates: FormUpdateParams) => void
  disabled?: boolean
  /** 已占用时段（逻辑0：结束时间选择器置灰用） */
  occupiedRanges?: OccupiedPeriod[]
  /** 排班校验状态（逻辑1）：undefined=未检验，true=有排班，false=无排班 */
  hasScheduling?: boolean
  /** 结束日期排班校验状态（跨天时）：undefined=同天或未检验，false=结束日期无排班 */
  endDateHasScheduling?: boolean
  /** 出差时长（逻辑2，小时） */
  businessLength?: number
  /** 提交按钮是否因排班缺失被禁用（由父组件控制） */
  submitDisabled?: boolean
}

/**
 * 同城表单组件
 * 注：同城不需要填写预估费用和同行人员
 */
const LocalTripForm: React.FC<LocalTripFormProps> = ({
  formData,
  onChange,
  disabled = false,
  occupiedRanges,
  hasScheduling,
  endDateHasScheduling,
  businessLength,
}) => {
  // 判断开始时间是否落在某个已占用时段内（开区间：sc < re AND sc >= rs）
  const allHoursDisabled = useMemo(() => {
    if (!occupiedRanges || occupiedRanges.length === 0 || !formData.startDate) return false
    const sc = dayjs(formData.startDate)
    return occupiedRanges.some(({ startDate, endDate }) => {
      const rs = dayjs(startDate)
      const re = dayjs(endDate)
      return !sc.isBefore(rs) && sc.isBefore(re)
    })
  }, [occupiedRanges, formData.startDate])

  // 开始时间变化时，若结束时间早于新开始时间则自动清空结束时间
  const handleStartDateChange = (value: string) => {
    const updates: FormUpdateParams = { startDate: value }
    if (value && formData.endDate && dayjs(formData.endDate).isBefore(dayjs(value))) {
      updates.endDate = ''
    }
    onChange(updates)
  }

  const startOfCurrentMonth = dayjs().startOf('month').format('YYYY-MM-DDTHH:mm')

  return (
    <div className="space-y-clay-lg">
      {/* 提示框 */}
      <div className="info-box">
        <p className="body-text font-semibold mb-2">
          说明
        </p>
        <p className="hint-text text-slate-600">
          不离开当前城市的因公外出申请，时长记入考勤
        </p>
      </div>

      {/* 开始时间 */}
      <DateTimePicker
        label="开始时间"
        value={formData.startDate}
        onChange={handleStartDateChange}
        placeholder="请选择开始日期和时间"
        disabled={disabled}
        minDate={startOfCurrentMonth}
      />

      {/* 排班校验提示（逻辑1） */}
      {hasScheduling === false && (
        <div data-testid="no-scheduling-warning" className="p-clay-md rounded-clay bg-red-50 border-l-4 border-red-400">
          <p className="text-clay-sm text-red-700 font-semibold">不在排班期，无法提交申请</p>
          <p className="text-clay-sm text-red-600">请修改外出时间在排班时间内，如仍需外出，请与HRBP确认排班。</p>
        </div>
      )}

      {/* 结束时间 */}
      <div>
        <DateTimePicker
          label="结束时间"
          value={formData.endDate}
          onChange={(value) => onChange({ endDate: value })}
          placeholder="请选择结束日期和时间"
          disabled={disabled || hasScheduling === false}
          minDate={formData.startDate || undefined}
          defaultTime={[18, 30]}
        />
        {allHoursDisabled && (
          <div data-testid="all-hours-disabled-warning" className="mt-2 p-clay-md rounded-clay bg-red-50 border-l-4 border-red-400">
            <p className="text-clay-sm text-red-700 font-semibold">开始时间与已有申请时段重叠</p>
            <p className="text-clay-sm text-red-600">
              所选开始时间与已有审批通过的申请时段重叠，请调整开始时间。
            </p>
          </div>
        )}
        {endDateHasScheduling === false && (
          <div data-testid="end-date-no-scheduling-warning" className="mt-2 p-clay-md rounded-clay bg-red-50 border-l-4 border-red-400">
            <p className="text-clay-sm text-red-700 font-semibold">不在排班期，无法提交申请</p>
            <p className="text-clay-sm text-red-600">结束日期当天无排班，请修改外出时间在排班时间内，如仍需外出，请与HRBP确认排班。</p>
          </div>
        )}
      </div>

      {/* 出差时长展示（逻辑2） */}
      {businessLength !== undefined && businessLength > 0 && (
        <div data-testid="business-length" className="info-box border-l-4 border-blue-400">
          <p className="body-text text-blue-700">
            预计出差时长：<span className="font-semibold">{businessLength.toFixed(1)} 小时</span>
          </p>
        </div>
      )}
      {businessLength === 0 && formData.endDate && hasScheduling !== false && endDateHasScheduling !== false && (
        <div data-testid="zero-business-length-warning" className="p-clay-md rounded-clay bg-red-50 border-l-4 border-red-400">
          <p className="text-clay-sm text-red-700 font-semibold">不在排班期，无法提交申请</p>
          <p className="text-clay-sm text-red-600">请修改外出时间在排班时间内，如仍需外出，请与HRBP确认排班。</p>
        </div>
      )}

      {/* 外出地点 */}
      <div className="required-field">
        <label className="heading-2 mb-clay-sm block">外出地点 <span className="text-red-500">*</span></label>
        <input
          type="text"
          placeholder="如：公司、客户办公室等"
          className="clay-input"
          maxLength={50}
          value={formData.location}
          onChange={(e) => {
            onChange({ location: e.target.value })
          }}
          disabled={disabled}
          readOnly={disabled}
        />
        <p className="hint-text mt-1">
          {formData.location?.length || 0}/50 字符
        </p>
      </div>

      {/* 外出事由 */}
      <div className="required-field">
        <label className="heading-2 mb-clay-sm block">外出事由 <span className="text-red-500">*</span></label>
        <textarea
          placeholder="请详细说明外出原因和主要工作内容"
          className="clay-textarea"
          rows={3}
          maxLength={200}
          value={formData.reason}
          onChange={(e) => onChange({ reason: e.target.value })}
          disabled={disabled}
          readOnly={disabled}
        />
        <p className="hint-text mt-1">
          {formData.reason?.length || 0}/200 字符
        </p>
      </div>

      {/* 是否需要火车票 - 暂时隐藏，后续再开放 */}
      {/* <div>
        <label className="clay-label">是否需要火车票 <span className="text-red-500">*</span></label>
        <select
          className="clay-input w-full appearance-none bg-white"
          value={formData.trainNeeded ? 'yes' : 'no'}
          onChange={(e) => onChange({ trainNeeded: e.target.value === 'yes' })}
          disabled={disabled}  // 🔑 查看模式禁用
        >
          <option value="no">否</option>
          <option value="yes">是</option>
        </select>
      </div> */}

      {/* 同城不需要填写预估费用和同行人员 */}
    </div>
  )
}

export default LocalTripForm
